import dayjs from 'dayjs';
import { Formik, FormikHelpers } from 'formik';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCampaign, useUpdateCampaign } from '../../../../service/hooks';
import { MLS_SOURCE } from '../../../../utils/constants';
import CampaignForm from '../forms/CampaignForm';
import { CAMPAIGN_FORM_VALUES, CAMPAIGN_SCHEMA } from '../forms/schema';

const EditCampaign: FC = () => {
  const navigate = useNavigate();
  const { mutate: updateCampaign } = useUpdateCampaign();
  const params = useParams();
  const { data: campaign } = useGetCampaign(params?.id as string);

  const onSubmit = (
    values: CAMPAIGN_FORM_VALUES,
    { setSubmitting, resetForm }: FormikHelpers<CAMPAIGN_FORM_VALUES>,
  ) => {
    const {
      listingContractDateFrom,
      listingContractDateTo,
      listingContractDateGreaterThanDays,
      listingContractDateLessThanDays,
      city,
      stateOrProvince,
      listPriceFrom,
      listPriceTo,
      yearBuiltFrom,
      yearBuiltTo,
      radiusInMiles,
      subdivisionName,
      listOfficeName,
      newConstructionYN,
      ownership,
      seniorCommunityYN,
      standardPropertyTypes,
      pendingTimestampTo,
      pendingTimestampFrom,
      offMarketTimestampTo,
      offMarketTimestampFrom,
      standardStatus,
      listAgentEmailOmitIfNotExists,
      mlsSource,
      ...rest
    } = values;

    const payload = {
      campaignSearchQuery: {
        mlsSource,
        listingContractDateFrom: listingContractDateFrom ? dayjs(listingContractDateFrom).toISOString() : undefined,
        listingContractDateTo: listingContractDateTo ? dayjs(listingContractDateTo).toISOString() : undefined,
        listingContractDateGreaterThanDays,
        listingContractDateLessThanDays,
        city,
        stateOrProvince,
        listPriceFrom,
        listPriceTo,
        yearBuiltFrom,
        yearBuiltTo,
        radiusInMiles,
        subdivisionName,
        listOfficeName,
        newConstructionYN,
        ownership,
        seniorCommunityYN,
        standardPropertyTypes,
        pendingTimestampTo,
        pendingTimestampFrom,
        offMarketTimestampTo,
        offMarketTimestampFrom,
        standardStatus,
        listAgentEmailOmitIfNotExists,
      },
      userId: campaign?.userId,
      ...rest,
    };

    updateCampaign(
      { ...payload, id: params?.id as string },
      {
        onSuccess: () => {
          resetForm();
          navigate('/');
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  const { campaignSearchQuery, ...rest } = campaign ?? {};
  const {
    listingContractDateFrom,
    listingContractDateTo,
    listingContractDateGreaterThanDays,
    listingContractDateLessThanDays,
    city,
    stateOrProvince,
    listPriceFrom,
    listPriceTo,
    yearBuiltFrom,
    yearBuiltTo,
    radiusInMiles,
    subdivisionName,
    listOfficeName,
    newConstructionYN,
    ownership,
    seniorCommunityYN,
    standardPropertyTypes,
    standardStatus,
    listAgentEmailOmitIfNotExists,
    mlsSource,
  } = campaignSearchQuery ?? {};

  if (!campaign) {
    return <div />;
  }

  return (
    <div className="mt-16">
      <Formik
        initialValues={
          {
            name: campaign?.name ?? '',
            mlsSource: mlsSource ?? MLS_SOURCE.V1,
            stateOrProvince: stateOrProvince ?? '',
            city: city ?? '',
            radiusInMiles: radiusInMiles ?? 0,
            listingContractDateFrom,
            listingContractDateTo,
            listingContractDateGreaterThanDays,
            listingContractDateLessThanDays,
            listPriceFrom,
            listPriceTo,
            yearBuiltFrom,
            yearBuiltTo,
            subdivisionName,
            listOfficeName,
            newConstructionYN,
            ownership,
            seniorCommunityYN,
            standardPropertyTypes,
            standardStatus,
            listAgentEmailOmitIfNotExists,
            ...rest,
          } as CAMPAIGN_FORM_VALUES
        }
        onSubmit={onSubmit}
        validationSchema={CAMPAIGN_SCHEMA}
      >
        {() => <CampaignForm />}
      </Formik>
    </div>
  );
};

export default EditCampaign;
